/* eslint-disable @nx/enforce-module-boundaries */
import { EditableComponent, ResponsiveGrid } from '@adobe/aem-react-editable-components';
import { FC, Suspense, useEffect, useState, useMemo, useContext, ReactNode } from 'react';
import clsx from 'clsx';
import { useQuery } from '@apollo/client';
import {
  respGridUtil,
  updateAEMCQKeys,
  VariableColumnContainer as VariableColumnContainerMolecule,
} from '@marriott/mi-ui-library';
import { getMboxParamters, fetchDCADynamicModelForAlloy, useSession } from '@marriott/mi-headless-utils';
import { phoenixCobrandECMMemberDetailsQuery } from '@marriott/mi-cobrand-graphql';

import { CobrandWrapperSkeletonLoader } from './CobrandWrapperSkeletonLoader';
import { AEMReactCompMap, useAlloyTargetMethod } from '../../modules/utils';
import { WrapperProps, CobrandWrapperProps } from './CobrandWrapper.types';
import { StyledCobrandWrapper } from './CobrandWrapper.styles';
import { PageContext } from '../../context/PageContext';
import { CobrandEcmContext, CobrandEcmContextValue, EcmMemberDetailsResponse } from '../../context/CobrandEcmContext';

const CACHE_KEY_PREFIX = 'alloy_target_wrapper_';
const CACHE_DURATION = 5 * 60 * 1000;

const ECM_HUB_AREA_CLASSES = ['item-primary', 'item-secondary', 'item-left', 'item-right', 'item-bottom'];

const getCachedTargetData = (mbox: string) => {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const cached = sessionStorage.getItem(`${CACHE_KEY_PREFIX}${mbox}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_DURATION) {
      sessionStorage.removeItem(`${CACHE_KEY_PREFIX}${mbox}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

const setCachedTargetData = (mbox: string, data: unknown) => {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(`${CACHE_KEY_PREFIX}${mbox}`, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // Silently fail if storage is full
  }
};

export const CobrandWrapperConfig = {
  emptyLabel: 'CobrandWrapper',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/cobrandwrapper`,
};

/**
 * Centralizes the ECM member-details UXL call for the ECM variation so child
 * organisms (CobrandWebSmallBanner, DataTiles) no longer each make their own
 * duplicate request. The query only runs when the ECM variation is enabled, the
 * component is not in author mode, and a `customerId` is available from the
 * session. Returns `null` when the ECM variation is off so children keep their
 * standalone behavior.
 */
const useEcmMemberData = (isEcmEnabled: boolean, isAuthorMode?: boolean): CobrandEcmContextValue => {
  const pageContext = useContext(PageContext);
  const sessionData = pageContext?.sessionData?.cacheData?.data;
  const customerId = sessionData?.consumerID;
  const currentLocale = (pageContext?.currentLocale ?? 'en_US') as string;
  const accessToken = (sessionData?.accessToken ?? pageContext?.accessToken) as string | undefined;

  const apolloContext = useMemo(() => {
    const headers: Record<string, string> = { 'accept-language': currentLocale.replace('_', '-') };
    if (process.env['IS_LOCAL_DEV'] === 'true' && accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    return { headers };
  }, [currentLocale, accessToken]);

  const {
    data: ecmData,
    loading: ecmLoading,
    error: ecmError,
  } = useQuery<EcmMemberDetailsResponse>(phoenixCobrandECMMemberDetailsQuery, {
    variables: { customerId },
    context: apolloContext,
    skip: !isEcmEnabled || !!isAuthorMode || !customerId,
    fetchPolicy: 'no-cache',
  });

  return useMemo(
    () => ({ isEcmEnabled, ecmData, ecmLoading, ecmError }),
    [isEcmEnabled, ecmData, ecmLoading, ecmError]
  );
};

/**
 * Wraps the wrapper's children with the shared ECM context only when the ECM
 * variation is enabled. Non-ECM renders return the children unchanged so there
 * is no behavioral or provider overhead for the default variation.
 */
const withEcmProvider = (isEcmEnabled: boolean, value: CobrandEcmContextValue, children: ReactNode): ReactNode => {
  if (!isEcmEnabled) {
    return children;
  }
  return <CobrandEcmContext.Provider value={value}>{children}</CobrandEcmContext.Provider>;
};

export const DCADecision: FC<Record<string, unknown>> = props => {
  const { targetMbox } = props;
  const { isTargetContentLoaded, targetData } = useAlloyTargetMethod({
    targetMboxValue: targetMbox as string,
    includeDSTParameters: false,
  });
  const { datalayer, mvpOffers } = useSession();

  const [model, setModel] = useState({ ...props, isTargetContentLoaded });
  const [isUpdated, setIsUpdated] = useState(false);

  const processedTargetData = useMemo(() => {
    const targetDataObj = targetData as Record<string, unknown>;
    if (!isTargetContentLoaded || !targetDataObj['cobrandwrapper']) return null;

    const cachedData = getCachedTargetData(targetMbox as string);
    if (cachedData) {
      return { cached: true, data: cachedData };
    }

    const respGridUtilMap = respGridUtil(targetDataObj['cobrandwrapper']);
    const { mboxParameters } = getMboxParamters(respGridUtilMap);
    return { cached: false, respGridUtilMap, mboxParameters };
  }, [isTargetContentLoaded, targetData, targetMbox]);

  useEffect(() => {
    if (!processedTargetData || isUpdated || !datalayer) {
      if (isTargetContentLoaded && !processedTargetData && !isUpdated) {
        setModel({ ...model, isTargetContentLoaded: true });
        setIsUpdated(true);
      }
      return;
    }

    if (processedTargetData.cached) {
      setModel({ ...model, ...processedTargetData.data, isTargetContentLoaded: true });
      setIsUpdated(true);
      return;
    }

    const { respGridUtilMap, mboxParameters } = processedTargetData;
    if (mboxParameters) {
      fetchDCADynamicModelForAlloy(mboxParameters, respGridUtilMap, datalayer, mvpOffers)?.then(res => {
        setCachedTargetData(targetMbox as string, res);
        setModel({ ...model, ...res, isTargetContentLoaded: true });
        setIsUpdated(true);
      });
    }
  }, [processedTargetData, isUpdated, datalayer, mvpOffers, targetMbox, model, isTargetContentLoaded]);

  return <CobrandWrapperComponent {...(model as unknown as WrapperProps)} />;
};

DCADecision.displayName = 'DCADecision';

export const CobrandWrapperComponent: FC<WrapperProps> = props => {
  const {
    targetMbox,
    header,
    headerTag,
    subHeader,
    ctaLabel,
    ctaLink,
    eyebrow,
    ctaType,
    trackingProperties,
    styleclass,
    componentID,
    itemPath,
    isAuthorMode,
    desktopColumns,
    tabletColumns,
    mobileColumns,
    reverseStacking,
    contentAlignment,
    headerFontSize,
    subFontSize,
    subHeaderTag,
    descriptionText,
    descFontSize,
    isCardComparisonFeatureEnabled,
    resolvedUrl = 'credit-cards',
    targetExperience,
    isTargetContentLoaded,
    enableFiveCardLayout,
    pagePath,
    parentOfCobrandWrapper,
    isParentOfCobrandWrapper,
    enableEcmHubDashboard,
  } = props;

  const updatedProps = updateAEMCQKeys(props);
  const cqItems = updatedProps['cqItems'];
  const col = getColumnsObj(desktopColumns, tabletColumns, mobileColumns);
  const isFiveCardLayout = enableFiveCardLayout === 'true' || enableFiveCardLayout === true;
  const isEcmHubDashboard = enableEcmHubDashboard === true;

  const ecmContextValue = useEcmMemberData(isEcmHubDashboard, isAuthorMode);

  const formWrapperArray = () => {
    return col.map((item, i) => {
      const columnClass = `col-xl-${item.deskVal} col-md-${item.tabVal} col-${item.mobVal}  ${
        isFiveCardLayout &&
        (parentOfCobrandWrapper === false || parentOfCobrandWrapper === 'false' || isParentOfCobrandWrapper === 'false')
          ? 'container-wrapper'
          : 'column-containers px-2 px-md-0'
      }`;
      return [`variable-column-${i}`, columnClass];
    });
  };

  const wrapperArray = formWrapperArray();
  const mapper = AEMReactCompMap(props.allowedComponents);
  const authorComponentWrapper = (index: number) => {
    const gridProps = {
      isInEditor: isAuthorMode,
      columnCount: '12',
      gridClassNames: '',
      pagePath,
      itemPath: `${itemPath}/${wrapperArray[index][0]}`,
      columnClassNames: {},
      allowedComponents: props.allowedComponents as never,
      customClassName: '',
      config: {
        isEmpty: () => true,
        resourceType: 'mi-aem-common-spa/components/container',
      },
    };
    return <ResponsiveGrid {...(gridProps as Parameters<typeof ResponsiveGrid>[0])} />;
  };

  const publishComponentWrapper = (columnName: string, jsonData: typeof cqItems, columnClass: string) => {
    const objToMap = props.cqItems || jsonData;
    const column = objToMap?.[columnName];
    if (!column) return null;

    const columnItems = column[':items'];
    const columnOrder = column[':itemsOrder'] || [];
    const componentsArray: JSX.Element[] = [];
    const isPersonalWrapper = Object.keys(columnItems).some(str => str.includes('cardcarousel'));

    // Check if 'custom-padding' class is present in styleclass
    const hasCustomPadding = styleclass?.split(/\s+/).includes('custom-padding') || false;

    columnOrder.forEach((itemKey: string) => {
      if (!(itemKey in columnItems)) return;

      const item = columnItems[itemKey] as Record<string, unknown>;
      const itemType = (item[':type'] as string)?.split('/').pop();

      if (itemType && itemType in mapper) {
        // ECM lays items out via CSS grid areas. All ECM items live inside a
        // single column's layout-wrapper, so the grid-area class sits on each
        // child (order: primary, secondary, left, right, bottom).
        const ecmVariationClass = isEcmHubDashboard ? ECM_HUB_AREA_CLASSES[componentsArray.length] ?? '' : '';

        const itemProps = {
          ...item,
          allowedComponents: props.allowedComponents,
          resolvedUrl,
          isCardComparisonFeatureEnabled: String(isCardComparisonFeatureEnabled) === 'true',
          layout: isFiveCardLayout ? '5-card' : '',
          cardTag: isPersonalWrapper && isFiveCardLayout ? 'personal' : '',
          targetExperience,
          hasCustomPadding, // Pass the custom padding flag
          enableECMHubDashboard: isEcmHubDashboard,
        };

        const reqJsonData = { ...column, ':items': { [itemKey]: itemProps } };

        componentsArray.push(
          <ResponsiveGrid
            model={respGridUtil(reqJsonData)}
            {...respGridUtil(reqJsonData)}
            key={itemKey}
            className={ecmVariationClass}
          />
        );
      }
    });

    if (!componentsArray.length) return null;

    return (
      <li
        key={columnName}
        className={clsx(
          columnClass,
          isFiveCardLayout && (!isPersonalWrapper ? 'business-wrapper pb-3' : 'personal-wrapper px-0')
        )}
      >
        <div className="layout-wrapper">
          {isFiveCardLayout && !isPersonalWrapper && <div className="card-tag business-badge"> Business</div>}
          {componentsArray}
        </div>
      </li>
    );
  };

  return (
    <StyledCobrandWrapper data-component-name="o-cobrand-CobrandWrapper" data-testid="cobrand-CobrandWrapper">
      <div
        className={clsx(
          isFiveCardLayout &&
            parentOfCobrandWrapper !== true &&
            parentOfCobrandWrapper !== 'true' &&
            isParentOfCobrandWrapper !== 'true' &&
            'no-list-style px-2 px-lg-0',
          'cobrand-wrapper',
          isEcmHubDashboard && 'ecm-hub-dashboard'
        )}
      >
        <Suspense fallback="">
          {targetMbox && !isTargetContentLoaded && !isAuthorMode ? (
            <CobrandWrapperSkeletonLoader {...props} />
          ) : (
            withEcmProvider(
              isEcmHubDashboard,
              ecmContextValue,
              <VariableColumnContainerMolecule
                componentId={`vcc${componentID}`}
                subHeader={subHeader}
                styleclass={styleclass}
                eyebrow={eyebrow}
                trackingProperties={trackingProperties}
                ctaLabel={ctaLabel}
                header={header}
                headerTag={headerTag}
                ctaLink={ctaLink}
                ctaType={ctaType}
                isAuthor={isAuthorMode}
                desktopColumns={desktopColumns}
                tabletColumns={tabletColumns}
                mobileColumns={mobileColumns}
                reverseStacking={reverseStacking}
                contentAlignment={contentAlignment}
                headerFontSize={headerFontSize}
                subFontSize={subFontSize}
                subHeaderTag={subHeaderTag}
                descriptionText={descriptionText}
                descFontSize={descFontSize}
              >
                {isAuthorMode &&
                  wrapperArray.map((_, i) => (
                    <div key={`author-wrapper-${i}`} className={wrapperArray[i][1]}>
                      {authorComponentWrapper(i)}
                    </div>
                  ))}
                {!isAuthorMode &&
                  wrapperArray.map((columnName, i) =>
                    publishComponentWrapper(columnName[0], cqItems, wrapperArray[i][1])
                  )}
              </VariableColumnContainerMolecule>
            )
          )}
        </Suspense>
      </div>
    </StyledCobrandWrapper>
  );
};

CobrandWrapperComponent.displayName = 'CobrandWrapperComponent';

export const CobrandWrapperEditableComponent: FC<{ model?: WrapperProps }> = props => {
  const { model } = props;
  const cqType = model?.componentName || (model as Record<string, unknown>)?.[':type'];
  const componentName = typeof cqType === 'string' ? cqType.split('/').pop() : '';

  return (
    <EditableComponent config={CobrandWrapperConfig} {...props}>
      <DCADecision {...props} componentName={componentName} />
    </EditableComponent>
  );
};

CobrandWrapperEditableComponent.displayName = 'CobrandWrapperEditableComponent';

export const CobrandWrapper: FC<CobrandWrapperProps> = props => {
  const { model } = props;

  return (
    <div
      data-testid="cobrand-CobrandWrapper"
      data-component-name="o-cobrand-CobrandWrapper"
      id={model?.componentID}
      className={clsx(
        model?.styleclass?.includes('inverse') && 'inverse',
        model?.styleclass?.includes('fullbleed') && 'm-container-fullbleed',
        model?.styleclass?.includes('standard') && 'standard t-background-color'
      )}
      style={{ clear: 'both' }}
    >
      <CobrandWrapperEditableComponent {...props} />
    </div>
  );
};

CobrandWrapper.displayName = 'CobrandWrapper';

export const createColumn = (deskVal: number, tabVal: number, mobVal = 12) => ({
  deskVal,
  tabVal,
  mobVal,
});

export const getColumnsObj = (desktopColumns = '', tabletColumns = '', mobileColumns = '') => {
  if (!desktopColumns && !tabletColumns && !mobileColumns) return [];

  const col: ReturnType<typeof createColumn>[] = [];
  const [desktopColVals, tabletColVals] = [desktopColumns.split(','), tabletColumns.split(',')];

  if (desktopColVals.length === 1 && tabletColVals.length === 1) {
    const deskTopCols = parseInt(desktopColumns, 10);
    const tabCols = parseInt(tabletColumns, 10);
    const mobCols = parseInt(mobileColumns, 10);

    const xlVal = 12 / deskTopCols;
    const mdVal = 12 / tabCols;
    const smVal = 12 / mobCols;

    for (let i = 0; i < deskTopCols; i++) {
      col.push(createColumn(xlVal, mdVal, smVal));
    }
  } else if (desktopColVals.length > 1 && tabletColVals.length > 1) {
    const mobCols = parseInt(mobileColumns, 10);
    const smVal = 12 / mobCols;

    for (let i = 0; i < parseInt(desktopColVals[0], 10); i++) {
      col.push(createColumn(parseInt(desktopColVals[i + 1], 10), parseInt(tabletColVals[i], 10), smVal));
    }
  } else if (desktopColVals.length > 1 && tabletColVals.length === 1) {
    const tabCols = parseInt(tabletColumns, 10);
    const mdVal = 12 / tabCols + 1;
    const mobCols = parseInt(mobileColumns, 10);
    const smVal = 12 / mobCols;

    for (let i = 0; i < parseInt(desktopColVals[0], 10); i++) {
      col.push(createColumn(parseInt(desktopColVals[i + 1], 10), mdVal, smVal));
    }
  } else if (desktopColVals.length === 1 && tabletColVals.length > 1) {
    const deskTopCols = parseInt(desktopColumns, 10);
    const xlVal = 12 / deskTopCols;
    const mobCols = parseInt(mobileColumns, 10);
    const smVal = 12 / mobCols;

    for (let i = 0, j = 0; i < deskTopCols; i++, j++) {
      if (j === tabletColVals.length) {
        j = 0;
      }
      col.push(createColumn(xlVal, parseInt(tabletColVals[j], 10), smVal));
    }
  }

  return col;
};
